import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { PlusIcon, Landmark, ChevronLeft, Globe } from 'lucide-react';
import { AxiosError } from 'axios';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAccountList, accountListKeys } from '@/hooks/use-account-list';
import { invalidatePortfolioQueries } from '@/lib/query-config';
import { AccountListPanel } from '@/components/accounts/account-list-panel';
import { AccountDetailPanel } from '@/components/accounts/account-detail-panel';
import { AddAccountModal } from '@/components/accounts/add-account-modal';
import { PlaidConnect } from '@/components/plaid-connect';
import type { Account } from '@/types/api';
import { DataUpdatingBanner } from '@/components/processing/DataUpdatingBanner';
import { EnableBankingConnect } from '@/components/enable-banking/connect-dialog';
import { EnableBankingMappingDialog } from '@/components/enable-banking/mapping-dialog';
import { PendingEnableBankingConnections } from '@/components/enable-banking/pending-connections';
import { useEnableBankingStatus } from '@/hooks/use-enable-banking';
import { useEnableBankingCallback } from '@/hooks/use-enable-banking-callback';

export default function AccountsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { accounts, plaidItems, isLoading, refetch } = useAccountList();
  // Enable Banking (#132): hidden entirely unless the tenant has credentials
  // (#148). Admins of a tenant without them get a link to set it up instead.
  const { data: enableBankingStatus } = useEnableBankingStatus();
  const enableBankingEnabled = enableBankingStatus?.enabled === true;
  const showEnableBankingSetup = enableBankingStatus?.enabled === false && enableBankingStatus.configurable;
  const { mappingConnectionId, openMapping, closeMapping } = useEnableBankingCallback();

  // Selection & modal state
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editAccount, setEditAccount] = useState<Account | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<
    { reason: 'HAS_TRANSACTIONS' | 'PLAID_CONNECTED' | 'UNKNOWN'; count?: number } | null
  >(null);

  const selectedAccount = accounts.find((a) => a.id === selectedAccountId) ?? null;

  // Refetch account list when window regains focus (catches post-Plaid-modal updates)
  useEffect(() => {
    const onFocus = () => {
      queryClient.invalidateQueries({ queryKey: accountListKeys.all });
      queryClient.invalidateQueries({ queryKey: accountListKeys.plaidItems() });
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [queryClient]);

  // ── Handlers ────────────────────────────────────────────────────────────

  const handleAddManual = () => {
    setEditAccount(null);
    setShowAddModal(true);
  };

  const handleEdit = () => {
    if (!selectedAccount) return;
    setEditAccount(selectedAccount.originalAccount);
    setShowAddModal(true);
  };

  const handleFormClose = useCallback(
    (refetchNeeded?: boolean) => {
      setShowAddModal(false);
      setEditAccount(null);
      if (refetchNeeded) refetch();
    },
    [refetch],
  );

  const handleDeleteConfirm = async () => {
    if (!selectedAccount) return;
    setIsDeleting(true);
    setDeleteError(null);
    try {
      await api.deleteAccount(selectedAccount.id);
      toast({
        title: t('accountsPage.deleteSuccess'),
        description: t('accountsPage.deleteSuccessDetail', { name: selectedAccount.accountName }),
      });
      setShowDeleteConfirm(false);
      setSelectedAccountId(null);
      refetch();
      // Deleting an account changes portfolio composition.
      invalidatePortfolioQueries(queryClient);
    } catch (err) {
      const ax = err as AxiosError<{ reason?: string; transactionCount?: number }>;
      if (ax?.response?.status === 409) {
        // Keep the dialog open and show the specific reason + remedy inline.
        const data = ax.response.data ?? {};
        if (data.reason === 'HAS_TRANSACTIONS') {
          setDeleteError({ reason: 'HAS_TRANSACTIONS', count: data.transactionCount });
        } else if (data.reason === 'PLAID_CONNECTED') {
          setDeleteError({ reason: 'PLAID_CONNECTED' });
        } else {
          setDeleteError({ reason: 'UNKNOWN' });
        }
      } else {
        setDeleteError({ reason: 'UNKNOWN' });
        toast({ title: t('accountsPage.deleteFailed'), variant: 'destructive' });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Layout ──────────────────────────────────────────────────────────────

  return (
    <>
      <div className="flex flex-col h-full">
        {/* Processing status (#100): recalculation in progress / updated X ago. */}
        <DataUpdatingBanner watch={['BANK_SYNC']} invalidate={[['account-list'], ['plaid-items'], ['accounts']]} className="mx-6 mt-4" />
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-6 py-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{t('accountsPage.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('accountsPage.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Uses PlaidConnect's built-in exchange + AccountSelectionModal flow */}
            <PlaidConnect variant="outline">
              <Landmark className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('accountsPage.connectBank')}</span>
            </PlaidConnect>
            <EnableBankingConnect variant="outline">
              <Globe className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('enableBanking.connect.button')}</span>
            </EnableBankingConnect>
            {showEnableBankingSetup && (
              <Button variant="outline" asChild>
                <Link to="/settings?tab=bank-sync" data-testid="eb-setup-link">
                  <Globe className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline">{t('enableBanking.credentials.setupLink')}</span>
                </Link>
              </Button>
            )}
            <Button onClick={handleAddManual}>
              <PlusIcon className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('accountsPage.addManual')}</span>
            </Button>
          </div>
        </div>

        {enableBankingEnabled && (
          <PendingEnableBankingConnections plaidItems={plaidItems} onOpenMapping={openMapping} className="px-6 pb-4" />
        )}

        <Separator />

        {/* Master-Detail Body */}
        <div className="flex flex-1 min-h-0">
          {/* Left — Account List (hidden on mobile when an account is selected) */}
          <div className={`w-full lg:w-[380px] lg:shrink-0 ${selectedAccountId ? 'hidden lg:block' : 'block'}`}>
            <AccountListPanel
              accounts={accounts}
              selectedAccountId={selectedAccountId}
              onSelectAccount={setSelectedAccountId}
              isLoading={isLoading}
            />
          </div>

          {/* Right — Detail (hidden on mobile when no account is selected) */}
          <div className={`flex-1 min-w-0 overflow-y-auto ${selectedAccountId ? 'block' : 'hidden lg:flex'}`}>
            {selectedAccount ? (
              <>
                {/* Back button — mobile only */}
                <div className="lg:hidden px-4 pt-3 pb-1">
                  <Button variant="ghost" size="sm" className="gap-1 -ml-2" onClick={() => setSelectedAccountId(null)}>
                    <ChevronLeft className="h-4 w-4" />
                    {t('common.back')}
                  </Button>
                </div>
                <AccountDetailPanel
                  account={selectedAccount}
                  onEdit={handleEdit}
                  onRefetch={refetch}
                  onOpenMapping={enableBankingEnabled ? openMapping : undefined}
                  onDelete={() => {
                    setDeleteError(null);
                    setShowDeleteConfirm(true);
                  }}
                />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-center p-10">
                <div className="bg-muted rounded-full p-4 mb-4">
                  <Landmark className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-medium">{t('accountsPage.selectAccount')}</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-sm">
                  {t('accountsPage.selectAccountHint')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add / Edit Account Modal */}
      <AddAccountModal
        open={showAddModal}
        onOpenChange={setShowAddModal}
        account={editAccount}
        onClose={handleFormClose}
      />

      {/* Enable Banking account mapping (#132) — opened from the bank callback or a connection */}
      {enableBankingEnabled && (
        <EnableBankingMappingDialog connectionId={mappingConnectionId} onClose={closeMapping} />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteConfirm}
        onOpenChange={(open) => {
          setShowDeleteConfirm(open);
          if (!open) setDeleteError(null);
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{t('accountsPage.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('accountsPage.deleteConfirm', { name: selectedAccount?.accountName })}
            </DialogDescription>
          </DialogHeader>

          {deleteError && (
            <p className="text-sm text-destructive">
              {deleteError.reason === 'HAS_TRANSACTIONS'
                ? t('accountsPage.deleteBlockedTransactions', { count: deleteError.count ?? 0 })
                : deleteError.reason === 'PLAID_CONNECTED'
                  ? t('accountsPage.deleteBlockedPlaid')
                  : t('accountsPage.deleteUnknownError')}
            </p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting || deleteError?.reason === 'HAS_TRANSACTIONS' || deleteError?.reason === 'PLAID_CONNECTED'}
            >
              {isDeleting ? t('ui.deleting') : t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
